import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import type { GateShape, Note } from '../core/chart';
import type { Beat, Seconds, Verdict } from '../core/types';
import { createBackdrop } from './backdrop';
import {
  MAX_BITS,
  TRAIL_SECONDS,
  bitAt,
  burstCount,
  createTrail,
  fadeOf,
  smooth,
  trailClear,
  trailIndex,
  trailPush,
  type Bit,
} from './burst';
import { BUTTER, STONE, VERDICT_COLORS, lerpColor } from './color';
import { createDress, trace, type Body } from './dress';
import { hash01 } from './ink';
import type { Look } from './look';
import type { Stage } from './stage';
import {
  INK_WIDTH,
  PULSE_WIDTH,
  beatPulse,
  clamp01,
  fieldPoint,
  fitOf,
  incomingAt,
  landingMotion,
  pipsLit,
  shadowAt,
  unitOf,
  type Fit,
  type Point,
  type Viewport,
} from './view';

export interface GateView {
  readonly note: Note;
  readonly lead: Beat;
  readonly current: boolean;
}

export interface PuckView {
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly shape: GateShape;
  readonly rotation: number;
  readonly shake: number;
  readonly visible: boolean;
  readonly holding: boolean;
}

export interface LandingView {
  readonly note: Note;
  readonly verdict: Verdict;
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly age: Seconds;
  readonly retired: Seconds | null;
  readonly points: number;
  readonly multiplier: number;
  readonly milestone: boolean;
}

export interface SceneFrame {
  readonly gates: readonly GateView[];
  readonly puck: PuckView | null;
  readonly landings: readonly LandingView[];
  readonly aim: Point | null;
  readonly beat: Beat;
  readonly time: Seconds;
  readonly reduced: boolean;
  readonly level: number;
}

export interface Scene {
  draw(frame: SceneFrame): void;
  clear(): void;
  destroy(): void;
}

const RETIRE_SECONDS = 0.35;
const SHAKE_HZ = 11;
const SHAKE_REACH = 0.025;
const HELD_INK = 0.85;
const IDLE_ALPHA = 0.85;
const AIM_RADIUS = 0.035;
const BLEND_SECONDS = 0.22;
const PATH_ALPHA = 0.16;
const PIP_RADIUS = 0.018;
const PIP_STEP = 0.055;
const PIP_GAP = 0.07;
const PIP_POP = 0.6;
const RING_SECONDS = 0.45;
const RING_GROW = 0.7;
const POPUP_SECONDS = 0.8;
const POPUP_RISE = 0.16;
const POPUP_SIZE = 0.085;
const POPUP_FONT = 48;
const LABELS = 8;
const GLOWS = 20;
const GLOW_TEXTURE = 128;
const GLOW_EASE = 0.18;
const LANDING_GLOW = 0.9;
const TRAIL_SLOTS = 24;
const TRAIL_STEP = 0.004;
const TRAIL_ALPHA = 0.6;
const MILESTONE_SECONDS = 1.1;

function glowTexture(): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = GLOW_TEXTURE;
  canvas.height = GLOW_TEXTURE;
  const ctx = canvas.getContext('2d');
  if (ctx !== null) {
    const half = GLOW_TEXTURE / 2;
    const fill = ctx.createRadialGradient(half, half, 0, half, half, half);
    fill.addColorStop(0, 'rgba(255,255,255,1)');
    fill.addColorStop(0.4, 'rgba(255,255,255,0.35)');
    fill.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, GLOW_TEXTURE, GLOW_TEXTURE);
  }
  return Texture.from(canvas);
}

export function createScene(stage: Stage, look: Look): Scene {
  const pal = look.palette;
  const backdrop = createBackdrop(stage.back, look);
  const glows = new Container();
  const trailG = new Graphics();
  const ink = new Graphics();
  const fx = new Graphics();
  const labels = new Container();
  fx.blendMode = look.glow;
  stage.world.addChild(glows, trailG, ink, fx, labels);
  const dress = createDress(ink, look);
  const halo = glowTexture();
  const glowPool = Array.from({ length: GLOWS }, () => {
    const sprite = new Sprite(halo);
    sprite.anchor.set(0.5);
    sprite.blendMode = look.glow;
    sprite.visible = false;
    glows.addChild(sprite);
    return sprite;
  });
  const pool = Array.from({ length: LABELS }, () => {
    const label = new Text({
      text: '',
      style: {
        fontFamily: `"${look.font}", ui-rounded, system-ui, sans-serif`,
        fontSize: POPUP_FONT,
        fontWeight: look.fontWeight as '400',
        fill: pal.text,
        stroke: { color: pal.bg, width: 8, join: 'round' },
      },
    });
    label.anchor.set(0.5);
    label.visible = false;
    labels.addChild(label);
    return label;
  });

  const trail = createTrail(TRAIL_SLOTS);
  const bit: Bit = { x: 0, y: 0, size: 0, rot: 0, alpha: 0, tone: 0 };
  const pathPoints: number[] = [];
  const spec = look.burst;
  const wear = look.fx;
  let glowUsed = 0;
  let bitsLeft = MAX_BITS;
  let puckGlow = 0;
  let lastTime = -1;

  const view = (): Viewport => ({ width: stage.width, height: stage.height });

  const glow = (x: number, y: number, radius: number, color: number, alpha: number): void => {
    if (alpha <= 0.002 || radius <= 0) return;
    const sprite = glowPool[glowUsed];
    if (sprite === undefined) return;
    glowUsed += 1;
    sprite.position.set(x, y);
    sprite.width = radius * 2;
    sprite.height = radius * 2;
    sprite.tint = color;
    sprite.alpha = Math.min(1, alpha);
    sprite.visible = true;
  };

  const spotBody = (note: Note, v: Viewport, unit: number, grow = 1): Body => {
    const spot = fieldPoint(v, note.x, note.y);
    return {
      shape: note.shape,
      x: spot.x,
      y: spot.y,
      radius: note.size * unit * grow,
      rotation: note.rotation,
      scaleX: 1,
      scaleY: 1,
    };
  };

  const drawPath = (gates: readonly GateView[], v: Viewport): void => {
    pathPoints.length = 0;
    for (const gate of gates) {
      if (gate.lead < 0) continue;
      const at = fieldPoint(v, gate.note.x, gate.note.y);
      pathPoints.push(at.x, at.y);
    }
    dress.path(pathPoints, pathPoints.length, PATH_ALPHA);
  };

  const drawTarget = (gate: GateView, v: Viewport, unit: number): void => {
    const shade = shadowAt(gate.note, gate.lead);
    if (shade === null || shade.alpha <= 0) return;
    const body = spotBody(gate.note, v, unit);
    if (gate.current) {
      const tone = look.dark ? pal.spot : BUTTER;
      glow(body.x, body.y, body.radius * (1.5 + shade.spread), tone, look.glowAlpha * 0.45 * shade.alpha);
    }
    dress.target(body, shade.alpha, shade.spread, gate.current);
  };

  const discOf = (gate: GateView, v: Viewport, unit: number): { body: Body; alpha: number; thick: number } | null => {
    const disc = incomingAt(gate.note, gate.lead);
    if (disc === null) return null;
    const at = fieldPoint(v, disc.x, disc.y);
    const radius = gate.note.size * unit * disc.scale;
    const body: Body = {
      shape: gate.note.shape,
      x: at.x,
      y: at.y,
      radius,
      rotation: gate.note.rotation,
      scaleX: 1,
      scaleY: disc.squash,
    };
    return { body, alpha: disc.alpha, thick: disc.thick * radius };
  };

  const drawDisc = (gate: GateView, v: Viewport, unit: number): void => {
    const disc = discOf(gate, v, unit);
    if (disc === null) return;
    glow(disc.body.x, disc.body.y, disc.body.radius * 1.7, pal.incoming[gate.note.shape], look.glowAlpha * 0.7 * disc.alpha);
    dress.incoming(disc.body, disc.alpha, disc.thick);
  };

  const drawRim = (gate: GateView, v: Viewport, unit: number, pulse: number): void => {
    const disc = discOf(gate, v, unit);
    if (disc === null) return;
    const scale = gate.note.size > 0 ? disc.body.radius / (gate.note.size * unit) : 1;
    const beat = gate.current ? 1 + PULSE_WIDTH * pulse : 1;
    dress.rim(disc.body, disc.alpha, INK_WIDTH * unit * scale * beat, gate.note.index);
  };

  const drawPips = (gate: GateView, v: Viewport, unit: number, beat: Beat, pulse: number): void => {
    if (!gate.current || gate.lead < 0) return;
    const shade = shadowAt(gate.note, gate.lead);
    if (shade === null) return;
    const count = gate.note.holdBeats;
    const lit = pipsLit(gate.note, beat);
    const row = fieldPoint(v, gate.note.x, gate.note.y + gate.note.size + PIP_GAP);
    const left = row.x - ((count - 1) * PIP_STEP * unit) / 2;
    for (let i = 1; i <= count; i++) {
      const on = i <= lit;
      const pop = on && i === lit ? 1 + PIP_POP * pulse : 1;
      dress.pip(left + (i - 1) * PIP_STEP * unit, row.y, PIP_RADIUS * unit * pop, on, i === count, shade.alpha);
    }
  };

  const drawLabels = (landings: readonly LandingView[], v: Viewport, unit: number, reduced: boolean): void => {
    let used = 0;
    const put = (text: string, at: Point, alpha: number, color: number, scale: number): void => {
      const label = pool[used++];
      if (label === undefined) return;
      if (label.text !== text) label.text = text;
      label.style.fill = color;
      label.position.set(at.x, at.y);
      label.alpha = alpha;
      label.scale.set(((POPUP_SIZE * unit) / POPUP_FONT) * scale);
      label.visible = true;
    };
    for (const landing of landings) {
      if (landing.points <= 0 || landing.age >= POPUP_SECONDS) continue;
      const t = clamp01(landing.age / POPUP_SECONDS);
      const rise = reduced ? 0 : POPUP_RISE * t;
      const note = landing.note;
      const fade = 1 - t * t;
      put(`+${String(landing.points)}`, fieldPoint(v, note.x, note.y - note.size - PIP_GAP - rise), fade, pal.text, 1);
      if (landing.multiplier > 1) {
        const side = fieldPoint(v, note.x + note.size + PIP_GAP, note.y - rise);
        put(`×${String(landing.multiplier)}`, side, fade, VERDICT_COLORS[landing.verdict], 0.8);
      }
    }
    for (const label of pool.slice(used)) label.visible = false;
  };

  const drawBurst = (landing: LandingView, fit: Fit, v: Viewport, unit: number, reduced: boolean): void => {
    const count = Math.min(bitsLeft, burstCount(fit, landing.milestone, reduced, spec.count));
    if (count <= 0 || landing.age >= spec.life) return;
    const center = fieldPoint(v, landing.note.x, landing.note.y);
    const tones = pal.sparks.length;
    let live = 0;
    for (let i = 0; i < count; i++) {
      if (!bitAt(bit, landing.note.index, i, landing.age, spec, landing.note.size, tones)) continue;
      live += 1;
      wear.bit(fx, center.x + bit.x * unit, center.y + bit.y * unit, bit.size * unit, i, pal.sparks[bit.tone] ?? BUTTER, bit.alpha, bit.rot);
    }
    bitsLeft -= live;
  };

  const drawMilestone = (landing: LandingView, v: Viewport, unit: number, reduced: boolean): void => {
    if (!landing.milestone || reduced) return;
    const fade = fadeOf(landing.age, MILESTONE_SECONDS);
    if (fade <= 0) return;
    const center = fieldPoint(v, landing.note.x, landing.note.y);
    const grow = smooth(landing.age / MILESTONE_SECONDS);
    const r = landing.note.size * unit * (1.1 + 0.9 * grow);
    wear.milestone(fx, { x: center.x, y: center.y, r, width: INK_WIDTH * unit * 0.7, fade, grow, unit }, pal);
  };

  const drawLanding = (landing: LandingView, v: Viewport, unit: number, reduced: boolean): void => {
    const life = landing.retired === null ? 1 : clamp01(1 - landing.retired / RETIRE_SECONDS);
    const fit = fitOf(landing.verdict);
    drawBurst(landing, fit, v, unit, reduced);
    drawMilestone(landing, v, unit, reduced);
    if (life <= 0) return;
    const motion = landingMotion(fit, landing.age, reduced);
    const spot = spotBody(landing.note, v, unit);
    const tone = VERDICT_COLORS[landing.verdict];
    if (fit === 'seat' || fit === 'gap') {
      glow(spot.x, spot.y, spot.radius * (landing.milestone ? 2.4 : 2), tone, look.glowAlpha * fadeOf(landing.age, LANDING_GLOW) * life);
    }
    trace(ink, spot);
    ink.stroke({
      width: INK_WIDTH * unit * 0.8,
      color: fit === 'pass' ? STONE : dress.outline,
      alpha: life * (fit === 'pass' ? motion.alpha : 0.8),
      join: 'round',
    });
    const ring = clamp01(landing.age / RING_SECONDS);
    if (!reduced && ring < 1 && (fit === 'seat' || fit === 'gap')) {
      trace(ink, spotBody(landing.note, v, unit, 1 + RING_GROW * ring));
      ink.stroke({ width: INK_WIDTH * unit * 1.5 * (1 - ring), color: tone, alpha: fadeOf(landing.age, RING_SECONDS) });
    }
    if (landing.size <= 0) return;
    const place = fieldPoint(v, landing.x, landing.y + motion.dy);
    const blend = reduced ? 1 : clamp01(landing.age / BLEND_SECONDS);
    const final = fit === 'seat' ? landing.note.size : landing.size;
    const radius = (landing.size + (final - landing.size) * blend) * unit;
    dress.mine(
      {
        shape: landing.note.shape,
        x: place.x,
        y: place.y,
        radius,
        rotation: landing.note.rotation,
        scaleX: motion.scaleX,
        scaleY: motion.scaleY,
      },
      life * motion.alpha,
      INK_WIDTH * unit * HELD_INK,
      lerpColor(pal.held, tone, blend),
      landing.note.index + 1,
    );
  };

  const drawTrail = (puck: PuckView | null, v: Viewport, unit: number, time: Seconds, reduced: boolean): void => {
    if (puck === null || !puck.visible) {
      trailClear(trail);
      return;
    }
    if (reduced) return;
    if (puck.holding) trailPush(trail, puck.x, puck.y, time, TRAIL_STEP);
    const radius = puck.size * unit;
    let drawn = 0;
    for (let back = 0; back < trail.size && drawn < bitsLeft; back++) {
      const k = trailIndex(trail, back);
      const age = time - trail.ts[k]!;
      if (age < 0 || age >= TRAIL_SECONDS) continue;
      const seed = Math.round(trail.ts[k]! * 1000);
      const fade = 1 - age / TRAIL_SECONDS;
      const at = fieldPoint(v, trail.xs[k]!, trail.ys[k]!);
      const angle = hash01(seed, k, 1) * Math.PI * 2;
      const reach = radius * (0.4 + 0.6 * hash01(seed, k, 2));
      const size = unit * (0.008 + 0.008 * hash01(seed, k, 3));
      const x = at.x + Math.cos(angle) * reach;
      const y = at.y + Math.sin(angle) * reach + age * unit * 0.05;
      const color = pal.sparks[Math.floor(hash01(seed, k, 4) * pal.sparks.length)] ?? BUTTER;
      wear.trail(trailG, x, y, size, angle);
      trailG.fill({ color, alpha: fade * fade * TRAIL_ALPHA });
      drawn += 1;
    }
    bitsLeft -= drawn;
  };

  const drawPuck = (puck: PuckView, v: Viewport, unit: number, time: Seconds, pulse: number, reduced: boolean, dt: number): void => {
    const goal = puck.visible && puck.size > 0 ? (puck.holding ? 1 : 0.6) : 0;
    puckGlow += (goal - puckGlow) * clamp01(dt / GLOW_EASE);
    if (!puck.visible || puck.size <= 0) return;
    const wobble = reduced ? 0 : Math.sin(time * SHAKE_HZ * 2 * Math.PI) * puck.shake * SHAKE_REACH;
    const spot = fieldPoint(v, puck.x + wobble, puck.y);
    const radius = puck.size * unit;
    glow(spot.x, spot.y, radius * 1.8, pal.held, look.glowAlpha * puckGlow);
    dress.mine(
      { shape: puck.shape, x: spot.x, y: spot.y, radius, rotation: puck.rotation, scaleX: 1, scaleY: 1 },
      puck.holding ? 1 : IDLE_ALPHA,
      INK_WIDTH * unit * HELD_INK * (1 + PULSE_WIDTH * pulse),
      pal.held,
      0,
    );
  };

  const hideGlows = (from: number): void => {
    for (let i = from; i < glowPool.length; i++) {
      const sprite = glowPool[i];
      if (sprite !== undefined) sprite.visible = false;
    }
  };

  return {
    draw(frame) {
      const v = view();
      const unit = unitOf(v);
      backdrop.sync(v.width, v.height, frame.level);
      ink.clear();
      trailG.clear();
      fx.clear();
      glowUsed = 0;
      bitsLeft = MAX_BITS;
      const dt = lastTime < 0 ? 0 : Math.max(0, Math.min(0.1, frame.time - lastTime));
      lastTime = frame.time;
      if (unit <= 0) {
        hideGlows(0);
        return;
      }
      dress.begin(unit, frame.time, frame.reduced);
      const pulse = frame.reduced ? 0 : beatPulse(frame.beat);
      const gates = [...frame.gates].sort((a, b) => b.lead - a.lead);
      drawPath(frame.gates, v);
      for (const landing of frame.landings) drawLanding(landing, v, unit, frame.reduced);
      for (const gate of gates) drawTarget(gate, v, unit);
      for (const gate of gates) drawDisc(gate, v, unit);
      drawTrail(frame.puck, v, unit, frame.time, frame.reduced);
      if (frame.puck !== null) drawPuck(frame.puck, v, unit, frame.time, pulse, frame.reduced, dt);
      else puckGlow = 0;
      for (const gate of gates) drawRim(gate, v, unit, pulse);
      for (const gate of gates) drawPips(gate, v, unit, frame.beat, pulse);
      if (frame.aim !== null) {
        const spot = fieldPoint(v, frame.aim.x, frame.aim.y);
        ink.circle(spot.x, spot.y, AIM_RADIUS * unit);
        ink.stroke({ width: INK_WIDTH * unit * 0.5, color: pal.text, alpha: 0.6 });
      }
      hideGlows(glowUsed);
      drawLabels(frame.landings, v, unit, frame.reduced);
    },
    clear() {
      ink.clear();
      trailG.clear();
      fx.clear();
      trailClear(trail);
      hideGlows(0);
      for (const label of pool) label.visible = false;
    },
    destroy() {
      backdrop.destroy();
      glows.destroy({ children: true });
      halo.destroy(true);
      trailG.destroy();
      ink.destroy();
      fx.destroy();
      labels.destroy({ children: true });
    },
  };
}
